import React, { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  ComicFormat,
  FORMATS,
  FORMAT_NAMES,
  OwnedFilter,
  ReadFilter,
  SORT_LABELS,
  SortKey,
} from '../types';
import { filterPanel } from '../../../utils/surfaces';

interface ComicFilterBarProps {
  isReadOnly: boolean;
  search: string;
  onSearch: (value: string) => void;
  format: ComicFormat | 'all';
  onFormat: (value: ComicFormat | 'all') => void;
  publisher: string;
  publishers: string[];
  onPublisher: (value: string) => void;
  sort: SortKey;
  sortOptions: SortKey[];
  onSort: (value: SortKey) => void;
  // Read and Owned dropdowns; the Reading tab doesn't need them
  showStatusFilters: boolean;
  readFilter: ReadFilter;
  onReadFilter: (value: ReadFilter) => void;
  ownedFilter: OwnedFilter;
  onOwnedFilter: (value: OwnedFilter) => void;
  // Resets search and every filter (not the sort)
  onClear: () => void;
  onNewGroup: () => void;
  onNewSeries: () => void;
  onAddComic: () => void;
}

const READ_FILTERS: [ReadFilter, string][] = [
  ['all', 'All'],
  ['read', 'Read'],
  ['unread', 'Unread'],
];

const OWNED_FILTERS: [OwnedFilter, string][] = [
  ['all', 'All'],
  ['owned', 'Owned'],
  ['notOwned', 'Not owned'],
];

const ComicFilterBar = ({
  isReadOnly,
  search,
  onSearch,
  format,
  onFormat,
  publisher,
  publishers,
  onPublisher,
  sort,
  sortOptions,
  onSort,
  showStatusFilters,
  readFilter,
  onReadFilter,
  ownedFilter,
  onOwnedFilter,
  onClear,
  onNewGroup,
  onNewSeries,
  onAddComic,
}: ComicFilterBarProps) => {
  const theme = useTheme();
  // Phones fold the filters away under one row; Add Comic stays in it
  const compact = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const [newAnchor, setNewAnchor] = useState<null | HTMLElement>(null);

  // Sort isn't a filter, so it doesn't count
  const activeCount = [
    search.trim() !== '',
    format !== 'all',
    publisher !== 'all',
    showStatusFilters && readFilter !== 'all',
    showStatusFilters && ownedFilter !== 'all',
  ].filter(Boolean).length;

  const fields = (
    <>
      <TextField
        size="small"
        placeholder="Search comics, series, writers…"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        sx={{ flex: '1 1 240px', minWidth: 0 }}
        slotProps={{
          htmlInput: { 'aria-label': 'Search comics' },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  aria-label="Clear search"
                  onClick={() => onSearch('')}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        }}
      />

      <FormControl size="small" sx={{ flex: '1 1 140px', maxWidth: 200 }}>
        <InputLabel id="comics-filter-format-label">Format</InputLabel>
        <Select
          labelId="comics-filter-format-label"
          value={format}
          label="Format"
          onChange={(e) => onFormat(e.target.value as ComicFormat | 'all')}
        >
          <MenuItem value="all">All formats</MenuItem>
          {FORMATS.map((entry) => (
            <MenuItem key={entry} value={entry}>
              {FORMAT_NAMES[entry]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ flex: '1 1 140px', maxWidth: 200 }}>
        <InputLabel id="comics-filter-publisher-label">Publisher</InputLabel>
        <Select
          labelId="comics-filter-publisher-label"
          value={publisher}
          label="Publisher"
          onChange={(e) => onPublisher(e.target.value)}
        >
          <MenuItem value="all">All publishers</MenuItem>
          {publishers.map((entry) => (
            <MenuItem key={entry} value={entry}>
              {entry}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ flex: '1 1 150px', maxWidth: 220 }}>
        <InputLabel id="comics-filter-sort-label">Sort</InputLabel>
        <Select
          labelId="comics-filter-sort-label"
          value={sort}
          label="Sort"
          onChange={(e) => onSort(e.target.value as SortKey)}>
          {sortOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {SORT_LABELS[option]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {showStatusFilters &&
        [
          { key: 'read', label: 'Read', options: READ_FILTERS, value: readFilter, onChange: onReadFilter },
          { key: 'owned', label: 'Owned', options: OWNED_FILTERS, value: ownedFilter, onChange: onOwnedFilter },
        ].map((group) => (
          <FormControl key={group.key} size="small" sx={{ flex: '1 1 120px', maxWidth: 170 }}>
            <InputLabel id={`comics-filter-${group.key}-label`}>{group.label}</InputLabel>
            <Select
              labelId={`comics-filter-${group.key}-label`}
              value={group.value}
              label={group.label}
              onChange={(e) => (group.onChange as (next: string) => void)(e.target.value)}
            >
              {(group.options as [string, string][]).map(([value, text]) => (
                <MenuItem key={value} value={value}>
                  {text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}
    </>
  );

  // Smaller on phones so all three buttons fit one row down to 360px
  const buttonSize = compact ? 'small' : 'medium';

  const actions = !isReadOnly && (
    <Box sx={{ display: 'flex', gap: compact ? 0.5 : 1, ml: 'auto', flexShrink: 0 }}>
      <Button
        variant="text"
        color="inherit"
        size={buttonSize}
        endIcon={<ArrowDropDownIcon />}
        onClick={(e) => setNewAnchor(e.currentTarget)}
        sx={{ whiteSpace: 'nowrap', ...(compact && { minWidth: 0, px: 0.5, '& .MuiButton-endIcon': { ml: 0 } }) }}
      >
        New
      </Button>
      <Button
        variant="contained"
        size={buttonSize}
        startIcon={<AddIcon />}
        onClick={onAddComic}
        sx={{ whiteSpace: 'nowrap', ...(compact && { px: 1.25, '& .MuiButton-startIcon': { mr: 0.5 } }) }}
      >
        Add Comic
      </Button>
    </Box>
  );

  const newMenu = (
  <Menu anchorEl={newAnchor} open={Boolean(newAnchor)} onClose={() => setNewAnchor(null)}>
    <MenuItem
      onClick={() => {
        setNewAnchor(null);
        onNewSeries();
      }}
    >
      New series
    </MenuItem>
    <MenuItem
      onClick={() => {
        setNewAnchor(null);
        onNewGroup();
      }}
    >
      New group
    </MenuItem>
  </Menu>
  );

  if (compact) {
    return (
      <Box sx={{ p: 1.25, mb: 3, ...filterPanel(theme) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Button
            color="inherit"
            size="small"
            startIcon={<TuneIcon />}
            endIcon={
              <ExpandMoreIcon
                sx={{ transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'none' }}
              />
            }
            onClick={() => setOpen((prev) => !prev)}
            aria-expanded={open}
            aria-controls="comics-filter-panel"
            sx={{
              whiteSpace: 'nowrap',
              flexShrink: 0,
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: 'action.hover',
              px: 1,
              '& .MuiButton-startIcon': { mr: 0.5 },
              '& .MuiButton-endIcon': { ml: 0.25 },
            }}
          >
            {activeCount ? `Filters · ${activeCount}` : 'Filters'}
          </Button>
          {actions}
        </Box>

        <Collapse in={open}>
          <Box
            id="comics-filter-panel"
            sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, pt: 1.5 }}
          >
            {fields}
            {activeCount > 0 && (
              <Button size="small" onClick={onClear} sx={{ ml: 'auto', textTransform: 'none' }}>
                Clear filters
              </Button>
            )}
          </Box>
        </Collapse>
        {newMenu}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        mb: 3,
        ...filterPanel(theme),
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      {fields}
      {actions}
      {newMenu}
    </Box>
  );
};

export default ComicFilterBar;
